import React from 'react';
import { CheckCircle, FileText, Shield, Clock, Headphones, ArrowRight } from 'react-feather';
import { IndianRupee } from 'lucide-react';
import Navbar from '../components/Landing/Navbar';
// Dummy image imports (replace with your actual images)
import taxConsulting from './images/tax-consulting.jpg';
import itrFiling from './images/itr-filing.jpg';
import tdsFiling from './images/tds-filing.jpg';
import taxExpert from './images/tax-expert.jpg';

const TaxServicesPage = () => {
  const services = [
    {
      title: "ITR-1 (Salaried Individuals)",
      description: "For individuals with income from salary, one house property, and other sources",
      price: "₹999",
      features: ["Form 16 Processing", "Tax Saving Advice", "Deduction Optimization", "E-verification Support"],
      image: itrFiling
    },
    {
      title: "ITR-2 (House Property & Capital Gains)",
      description: "For individuals with income from capital gains, foreign assets, or multiple properties",
      price: "₹1,999",
      features: ["Capital Gains Calculation", "Multiple Property Management", "Foreign Income Reporting", "Tax Liability Estimation"],
      image: taxConsulting
    },
    {
      title: "ITR-3 (Business Income)",
      description: "For individuals with income from business or profession",
      price: "₹2,999",
      features: ["Profit/Loss Statements", "Balance Sheet Preparation", "Business Deductions", "Audit Support"],
      image: itrFiling
    },
    {
      title: "ITR-4 (Presumptive Taxation)",
      description: "For small businesses and professionals opting for presumptive taxation",
      price: "₹1,499",
      features: ["Turnover Analysis", "Presumptive Income Calculation", "Tax Liability Estimation", "Compliance Check"],
      image: taxConsulting
    }
  ];

  const tdsServices = [
    {
      title: "TDS Return Filing (Form 24Q)",
      description: "Quarterly TDS returns for salaries",
      price: "₹1,299/quarter",
      features: ["Salary TDS Calculation", "Form 16 Generation", "Quarterly Return Filing", "Challan Verification"],
      image: tdsFiling
    },
    {
      title: "TDS Return Filing (Form 26Q)",
      description: "Quarterly TDS returns for non-salary payments",
      price: "₹1,299/quarter",
      features: ["Contractor Payments", "Rent TDS Deduction", "Interest Payments", "Quarterly Compliance"],
      image: tdsFiling
    },
    {
      title: "TDS Return Filing (Form 27Q)",
      description: "Quarterly TDS returns for NRIs",
      price: "₹1,999/quarter",
      features: ["NRI Tax Compliance", "DTAA Benefits", "Foreign Remittance", "Quarterly Filing"],
      image: tdsFiling
    },
    {
      title: "TDS Compliance Package",
      description: "End-to-end TDS management including deduction, deposit and returns",
      price: "₹4,999/year",
      features: ["Monthly TDS Deduction", "Quarterly Returns", "Annual Compliance", "Deduction Advisory"],
      image: tdsFiling
    }
  ];

  return (
    <>
        <Navbar/>

    <div className="bg-gray-50">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-r from-cyan-600 to-blue-600 text-white py-24 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-[url('./images/tax-pattern.jpg')] bg-repeat"></div>
        </div>
        <div className="max-w-7xl mx-auto text-center relative z-10">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">Professional Tax Filing & TDS Services</h1>
          <p className="text-xl max-w-3xl mx-auto">
            Get your taxes filed accurately by Chartered Accountants with 100% satisfaction guarantee
          </p>
          <div className="mt-8 flex flex-col sm:flex-row justify-center gap-4">
            <button className="bg-white text-cyan-600 hover:bg-gray-100 px-8 py-3 rounded-lg font-semibold text-lg shadow-md transition-all transform hover:-translate-y-1">
              File ITR Now
            </button>
            <button className="bg-transparent border-2 border-white hover:bg-white hover:text-cyan-600 px-8 py-3 rounded-lg font-semibold text-lg shadow-md transition-all transform hover:-translate-y-1">
              Free Consultation
            </button>
          </div>
        </div>
      </div>

      {/* Why Choose Us */}
      <div className="max-w-7xl mx-auto py-16 px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Why Choose TaxPro?</h2>
          <div className="w-20 h-1 bg-cyan-600 mx-auto mb-6"></div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Trusted by 50,000+ clients for accurate and hassle-free tax filing
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          <div className="bg-white p-8 rounded-xl shadow-md hover:shadow-lg transition-all transform hover:-translate-y-2">
            <div className="flex items-center mb-4">
              <div className="bg-cyan-100 p-2 rounded-full mr-4">
                <CheckCircle className="text-cyan-600 h-6 w-6" />
              </div>
              <h3 className="text-xl font-semibold">Accuracy Guarantee</h3>
            </div>
            <p className="text-gray-600">
              Our CAs verify every detail to ensure 100% accurate tax filing with maximum refunds
            </p>
          </div>

          <div className="bg-white p-8 rounded-xl shadow-md hover:shadow-lg transition-all transform hover:-translate-y-2">
            <div className="flex items-center mb-4">
              <div className="bg-cyan-100 p-2 rounded-full mr-4">
                <Shield className="text-cyan-600 h-6 w-6" />
              </div>
              <h3 className="text-xl font-semibold">Dedicated Support</h3>
            </div>
            <p className="text-gray-600">
              Get personal tax expert assistance throughout the process via chat, email or call
            </p>
          </div>

          <div className="bg-white p-8 rounded-xl shadow-md hover:shadow-lg transition-all transform hover:-translate-y-2">
            <div className="flex items-center mb-4">
              <div className="bg-cyan-100 p-2 rounded-full mr-4">
                <Clock className="text-cyan-600 h-6 w-6" />
              </div>
              <h3 className="text-xl font-semibold">Fast Processing</h3>
            </div>
            <p className="text-gray-600">
              Most returns filed within 48 hours with e-verification support for quick completion
            </p>
          </div>
        </div>
      </div>

      {/* ITR Services */}
      <div className="bg-white py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Income Tax Return Filing</h2>
            <div className="w-20 h-1 bg-cyan-600 mx-auto mb-6"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Select the right ITR form based on your income sources
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <div key={index} className="border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg transition-all transform hover:-translate-y-2 h-full flex flex-col">
                <div className="h-40 overflow-hidden">
                  <img src={service.image} alt={service.title} className="w-full h-full object-cover" />
                </div>
                <div className="bg-cyan-50 p-6 flex-grow">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{service.title}</h3>
                  <p className="text-gray-600 mb-4">{service.description}</p>
                  <div className="flex items-center">
                    <IndianRupee className="text-gray-700 h-5 w-5" />
                    <span className="text-2xl font-bold ml-1">{service.price}</span>
                    <span className="text-sm text-gray-500 ml-1">+ GST</span>
                  </div>
                </div>
                <div className="p-6 border-t border-gray-100">
                  <ul className="space-y-3 mb-6">
                    {service.features.map((feature, i) => (
                      <li key={i} className="flex items-start">
                        <CheckCircle className="text-green-500 h-5 w-5 mt-0.5 mr-2 flex-shrink-0" />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <button className="mt-auto w-full flex items-center justify-center px-4 py-3 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-cyan-600 hover:bg-cyan-700 transition-colors">
                    Select Plan <ArrowRight className="ml-2 h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* TDS Services */}
      <div className="bg-gray-50 py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">TDS Compliance Services</h2>
            <div className="w-20 h-1 bg-cyan-600 mx-auto mb-6"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Complete TDS management including deduction, deposit and return filing
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {tdsServices.map((service, index) => (
              <div key={index} className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all transform hover:-translate-y-2 h-full flex flex-col">
                <div className="h-40 overflow-hidden">
                  <img src={service.image} alt={service.title} className="w-full h-full object-cover" />
                </div>
                <div className="p-6 flex-grow">
                  <div className="mb-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{service.title}</h3>
                    <p className="text-gray-600 mb-4">{service.description}</p>
                  </div>
                  <div className="text-2xl font-bold text-gray-900 mb-4">{service.price}</div>
                  <ul className="space-y-3 mb-6">
                    {service.features.slice(0, 3).map((feature, i) => (
                      <li key={i} className="flex items-start">
                        <CheckCircle className="text-green-500 h-5 w-5 mt-0.5 mr-2 flex-shrink-0" />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="p-6 pt-0">
                  <button className="w-full flex items-center justify-center px-4 py-3 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-cyan-600 hover:bg-cyan-700 transition-colors">
                    Get Started <ArrowRight className="ml-2 h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Support Section */}
      <div className="bg-white py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-gradient-to-r from-cyan-50 to-blue-50 rounded-2xl p-8 md:p-12 relative overflow-hidden">
            <div className="absolute right-0 bottom-0 opacity-10">
              <img src={taxExpert} alt="Tax Expert" className="h-64" />
            </div>
            <div className="md:flex items-center justify-between relative z-10">
              <div className="md:w-2/3 mb-8 md:mb-0">
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">Need Help With Your Taxes?</h2>
                <p className="text-lg text-gray-600 mb-6">
                  Our team of Chartered Accountants is available to answer your questions and help you choose the right services
                </p>
                <div className="flex items-center">
                  <div className="bg-cyan-100 p-3 rounded-full mr-4">
                    <Headphones className="text-cyan-600 h-6 w-6" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Call our tax experts at</p>
                    <p className="text-xl font-semibold">1800-123-4567</p>
                  </div>
                </div>
              </div>
              <div className="md:w-1/3 flex justify-center md:justify-end">
                <button className="px-8 py-3 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold rounded-lg shadow-md transition-all transform hover:-translate-y-1">
                  Chat With Expert
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
        </>
  );
};

export default TaxServicesPage;